import { Card, CardHeader, CardContent } from '../ui/card';
import { observer } from 'mobx-react-lite';
import { useProfessionalViewModel } from '../../view-model/professional/professional-view-model';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import PasswordInputWithValidation from '../generics/password-input-with-validation';
import ConfirmPasswordInput from '../generics/confirm-password-input';
import AlertMessage from '../alert-message';
import { Trash2, AlertTriangle } from 'lucide-react';
import ConfirmationModal from '../ui/confirmation-modal';

const SecuritySettings = observer(() => {
  const professionalViewModel = useProfessionalViewModel();
  const { t } = useTranslation();
  
  // États de validation
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(false);
  const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(false);

  // État pour suivre si le mot de passe a été changé avec succès
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // État pour le modal de suppression de compte
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  
  // Vérifier si le formulaire est valide
  const isFormValid = () => {
    return professionalViewModel.isUpdate && 
           professionalViewModel.currentPassword.length > 0 && 
           isNewPasswordValid && 
           isConfirmPasswordValid &&
           !passwordChanged;
  };
  
  // Gérer le changement de mot de passe
  const handlePasswordUpdate = () => {
    professionalViewModel.handleUpdateSecuritySetting();
    setPasswordChanged(true);
    setCountdown(3);
  };
  
  // Compte à rebours pour la déconnexion
  useEffect(() => {
    if (passwordChanged && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [passwordChanged, countdown]);

  // Gérer la suppression du compte
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') return;

    setIsDeletingAccount(true);
    try {
      await professionalViewModel.handleDeleteAccount();
      // La redirection sera gérée par le view model
    } catch (error) {
      console.error('Error deleting account:', error);
      setIsDeletingAccount(false);
    }
  };

  return (
    <Card className="flex-1 overflow-hidden flex flex-col">
      <CardHeader>
        <h2 className="text-lg font-medium">{t('profile.security', 'Sécurité')}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('profile.securityDescription', 'Gérez vos informations de sécurité')}
        </p>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        <div className="space-y-6">
          {passwordChanged && (
            <AlertMessage 
              type="success" 
              message={`${t('profile.passwordChangeSuccess')}`}
            />
          )}
          <div>
            <h3 className="text-md font-medium mb-4">{t('profile.changePassword')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('profile.currentPassword')}</label>
                <input
                  disabled={!professionalViewModel.isUpdate || passwordChanged}
                  value={professionalViewModel.currentPassword}
                  onChange={e => professionalViewModel.setCurrentPassword(e.target.value)}
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <PasswordInputWithValidation
                  label={t('profile.newPassword')}
                  id="newPassword"
                  name="newPassword"
                  value={professionalViewModel.newPassword}
                  onChange={e => professionalViewModel.setNewPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  autoComplete="new-password"
                  disabled={!professionalViewModel.isUpdate || passwordChanged}
                  onValidationChange={setIsNewPasswordValid}
                  className="bg-white dark:bg-gray-800"
                />
              </div>
              <div>
                <ConfirmPasswordInput
                  label={t('profile.confirmNewPassword')}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={professionalViewModel.confirmPassword}
                  password={professionalViewModel.newPassword}
                  onChange={e => professionalViewModel.setConfirmPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  autoComplete="new-password"
                  disabled={!professionalViewModel.isUpdate || passwordChanged}
                  onValidationChange={setIsConfirmPasswordValid}
                  className="bg-white dark:bg-gray-800"
                />
              </div>
              <div className="pt-2">
                <button
                  onClick={handlePasswordUpdate}
                  type="button"
                  className={`w-full flex items-center justify-center px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors ${
                    !isFormValid()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary hover:bg-primary-dark'
                  }`}
                  disabled={!isFormValid()}
                >
                  {passwordChanged 
                    ? `${t('profile.redirecting', 'Redirection')}...` 
                    : t('profile.updatePassword', 'Mettre à jour le mot de passe')}
                </button>
              </div>
            </div>
          </div>

          {/* Zone de danger - Suppression de compte */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-900 dark:text-red-300 mb-2">
                    {t('profile.dangerZone', 'Zone de danger')}
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-4">
                    {t('profile.deleteAccountWarning', 'La suppression de votre compte est définitive et irréversible. Toutes vos données seront supprimées :')}
                  </p>
                  <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 mb-4 list-disc list-inside">
                    <li>{t('profile.deleteData.profile', 'Votre profil et informations personnelles')}</li>
                    <li>{t('profile.deleteData.companies', 'Toutes vos entreprises et services')}</li>
                    <li>{t('profile.deleteData.bookings', 'Vos réservations et disponibilités')}</li>
                    <li>{t('profile.deleteData.subscription', 'Votre abonnement (annulé à la fin de la période)')}</li>
                    <li>{t('profile.deleteData.stripe', 'Votre compte Stripe Connect')}</li>
                  </ul>
                </div>
              </div>

              <button
                onClick={() => setShowDeleteModal(true)}
                type="button"
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                {t('profile.deleteAccount', 'Supprimer mon compte')}
              </button>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
            onClick={() => !isDeletingAccount && setShowDeleteModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full">
              {/* En-tête du modal */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {t('profile.deleteAccountTitle', 'Supprimer votre compte')}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('profile.irreversibleAction', 'Cette action est irréversible')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contenu du modal */}
              <div className="p-6 space-y-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm text-red-900 dark:text-red-300 font-medium mb-2">
                    {t('profile.deleteWarningTitle', '⚠️ Attention : Cette action supprimera définitivement :')}
                  </p>
                  <ul className="text-sm text-red-800 dark:text-red-400 space-y-1 list-disc list-inside">
                    <li>{t('profile.deleteData.allPersonalData', 'Toutes vos données personnelles')}</li>
                    <li>{t('profile.deleteData.companiesAndCollaborators', 'Vos entreprises et collaborateurs')}</li>
                    <li>{t('profile.deleteData.servicesAndBookings', 'Tous vos services et réservations')}</li>
                    <li>{t('profile.deleteData.stripeSubscription', 'Votre abonnement Stripe')}</li>
                    <li>{t('profile.deleteData.stripeConnect', 'Votre compte Stripe Connect')}</li>
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.confirmDelete', 'Pour confirmer, tapez')} <span className="font-bold text-red-600 dark:text-red-400">SUPPRIMER</span>
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={t('profile.typeDelete', 'Tapez SUPPRIMER') || 'Tapez SUPPRIMER'}
                    disabled={isDeletingAccount}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Footer du modal */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeletingAccount}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'SUPPRIMER' || isDeletingAccount}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isDeletingAccount ? t('profile.deleting', 'Suppression...') : t('profile.deletePermanently', 'Supprimer définitivement')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </Card>
  );
});

export default SecuritySettings;
